import { useEffect, useRef } from 'react';
import { runOpportunityInitialization } from '../../../../../services/oneButtonServices/runOpportunityInitialization';
import { useOneButtonStore } from '../../../../../store/oneButtonStore';

interface UseOpportunityInitializationArgs {
    enabled: boolean;
    salesForceId: string | null | undefined;
    /** Bump to re-run initialization (e.g. manual refresh). */
    refreshKey?: number;
    waitForSiteDesign?: boolean;
}

/**
 * Coordinates critical Opportunity Information API loading and exposes store-backed
 * {@link isOpportunityInitializing} for the global page loader.
 */
export function useOpportunityInitialization({
    enabled,
    salesForceId,
    refreshKey = 0,
    waitForSiteDesign = true,
}: UseOpportunityInitializationArgs): void {
    const managedRef = useRef(false);

    useEffect(() => {
        if (!enabled) {
            if (managedRef.current) {
                useOneButtonStore.setState({ opportunityInitManagedByOrchestrator: false });
                managedRef.current = false;
            }
            return;
        }

        useOneButtonStore.setState({ opportunityInitManagedByOrchestrator: true });
        managedRef.current = true;

        return () => {
            if (managedRef.current) {
                useOneButtonStore.setState({ opportunityInitManagedByOrchestrator: false });
                managedRef.current = false;
            }
        };
    }, [enabled]);

    useEffect(() => {
        if (!enabled) return;

        const sfid = salesForceId?.trim();
        if (!sfid || sfid === '—') {
            useOneButtonStore.getState().resetOpportunityInitialization();
            return;
        }

        const requestId = useOneButtonStore.getState().beginOpportunityInitialization(sfid);

        void runOpportunityInitialization({
            salesForceId: sfid,
            requestId,
            waitForSiteDesign,
        });

        return () => {
            if (useOneButtonStore.getState().opportunityInitRequestId === requestId) {
                useOneButtonStore.setState({ isOpportunityInitializing: false });
            }
        };
    }, [enabled, salesForceId, refreshKey, waitForSiteDesign]);
}
